import { useContext, useState, useEffect } from 'react';
import { Eye, Search, X, History } from 'lucide-react';
import { Link } from 'react-router-dom';
import AuthContext from '../../../../../../context/AuthContext';
import { useToast } from '../../../../../../context/ToastContext';
import adminAuditService from '../../../../../../services/audit_services/adminAuditService';
import Pagination from '../../../../../../components/Pagination';

export default function AdminAuditProgramsList() {
  const { authTokens } = useContext(AuthContext);
  const { showError } = useToast();

  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [ordering, setOrdering] = useState('created_at_desc');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;

  useEffect(() => {
    // Fetch data on initial mount
    fetchPrograms();
  }, []);

  useEffect(() => {
    // Fetch when page changes
    if (currentPage > 1) {
      fetchPrograms();
    }
  }, [currentPage]);

  const fetchPrograms = async () => {
    setLoading(true);
    
    const params = {
      search: searchTerm,
      status: statusFilter,
      ordering: ordering,
      page: currentPage,
      page_size: pageSize
    };
    
    const result = await adminAuditService.getAuditPrograms(authTokens.access, params);
    
    if (result.success) {
      console.log('Programs data:', result.data);
      setPrograms(result.data.results || []);
      setTotalPages(result.data.total_pages || 1);
      setTotalCount(result.data.count || 0);
    } else {
      console.error('Error fetching programs:', result.error);
      showError(result.error || 'Erreur lors du chargement des programmes d\'audit');
      setPrograms([]);
    }
    
    setLoading(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchPrograms();
  };

  const handleApplyFilters = () => {
    setCurrentPage(1);
    fetchPrograms();
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setOrdering('created_at_desc');
    setCurrentPage(1);
    // Fetch with cleared filters
    setTimeout(() => fetchPrograms(), 0);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      opened: { bg: 'bg-green-100', text: 'text-green-800', label: 'Ouvert' },
      closed: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Fermé' },
      achieved: { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Archivé' }
    };

    const config = statusConfig[status] || statusConfig.opened;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des programmes d'audit...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Programmes d'audit</h1>
        <p className="text-sm text-gray-600 mt-1">Consultez tous les programmes d'audit de votre organisation</p>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rechercher
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleApplyFilters()}
                placeholder="Titre, objectif..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Statut
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="">Tous</option>
              <option value="opened">Ouvert</option>
              <option value="closed">Fermé</option>
              <option value="achieved">Archivé</option>
            </select>
          </div>

          {/* Ordering */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Trier par
            </label>
            <select
              value={ordering}
              onChange={(e) => setOrdering(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="created_at_desc">Plus récent</option>
              <option value="created_at_asc">Plus ancien</option>
              <option value="title_asc">Titre (A-Z)</option>
              <option value="title_desc">Titre (Z-A)</option>
            </select>
          </div>
        </div>

        {/* Apply Button */}
        <div className="mt-4 flex justify-end space-x-3">
          <button
            onClick={handleClearFilters}
            className="inline-flex items-center px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <X className="h-4 w-4 mr-2" />
            Effacer les filtres
          </button>
          <button
            onClick={handleApplyFilters}
            className="inline-flex items-center px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Search className="h-4 w-4 mr-2" />
            Appliquer les filtres
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {programs.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600">Aucun programme d'audit trouvé</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Détails du programme
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Parties
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Plans d'audit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Créé par
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {programs.map((program) => (
                  <tr key={program.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{program.title}</p>
                        <p className="text-xs text-gray-500 mt-1 truncate max-w-md">{program.objective}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          Créé le {new Date(program.created_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(program.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{program.parties_count || 0}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{program.audit_plans_count || 0}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600">{program.created_by_name || 'N/A'}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <Link
                          to={`/dashboard/admin/audits/programs/${program.id}`}
                          className="inline-flex items-center px-3 py-1  text-gray-500 text-sm rounded hover:bg-green-200 transition-colors"
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          
                        </Link>
                        <Link
                          to={`/dashboard/admin/audits/programs/${program.id}/history`}
                          className="inline-flex items-center px-3 py-1  text-gray-500 text-sm rounded hover:bg-blue-200 transition-colors"
                        >
                          <History className="h-4 w-4 mr-1" />
                          
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          totalCount={totalCount}
          pageSize={pageSize}
        />
      )}
    </div>
  );
}
